import { Link } from "react-router-dom";
import { Github, Linkedin, Twitter } from "lucide-react";
import EmiteLogo from "@/components/base/EmiteLogo";
import { publicDocsUrl } from "@/lib/docs";

interface FooterProps {
  darkMode?: boolean;
}

export default function Footer({ darkMode }: FooterProps) {
  return (
    <footer className={`mx-4 mb-4 rounded-2xl overflow-hidden ${darkMode ? "bg-slate-900" : "bg-slate-950"}`}>
      <div className="px-8 md:px-16 pt-16 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-16">
          {/* Brand */}
          <div className="md:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4 cursor-pointer">
              <EmiteLogo size={32} className="text-white" />
              <span className="font-bold text-lg text-white">emite.do</span>
            </Link>
            <p className="text-slate-400 text-sm leading-relaxed max-w-xs">
              Plataforma enterprise de facturación electrónica e-CF certificada DGII para República Dominicana.
            </p>
            <div className="flex items-center gap-2 mt-4">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse-slow inline-block" />
              <span className="text-sm text-emerald-400 font-medium">Todos los sistemas operativos</span>
            </div>
          </div>

          {/* Producto */}
          <div>
            <h4 className="text-white text-sm font-semibold uppercase tracking-widest mb-4">Producto</h4>
            <ul className="space-y-3">
              {[
                { label: "Características", href: "/#producto" },
                { label: "Precios", href: "/precios" },
                { label: "Contadores", href: "/contadores" },
                { label: "Comprobantes e-CF", href: "/#comprobantes" },
                { label: "Integraciones", href: "/#integraciones" },
                { label: "Changelog", href: publicDocsUrl("/changelog") },
              ].map((item) => (
                <li key={item.label}>
                  {item.href.startsWith("http") ? (
                    <a href={item.href} className="text-slate-400 text-sm hover:text-white transition-colors cursor-pointer">
                      {item.label}
                    </a>
                  ) : (
                    <Link to={item.href} className="text-slate-400 text-sm hover:text-white transition-colors cursor-pointer">
                      {item.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Docs */}
          <div>
            <h4 className="text-white text-sm font-semibold uppercase tracking-widest mb-4">Docs</h4>
            <ul className="space-y-3">
              {[
                { label: "Introducción", href: publicDocsUrl() },
                { label: "API Reference", href: publicDocsUrl("/api-reference") },
                { label: "Guía Odoo", href: publicDocsUrl("/guides/odoo") },
                { label: "Códigos de error", href: publicDocsUrl("/guides/errors") },
              ].map((item) => (
                <li key={item.label}>
                  <a href={item.href} className="text-slate-400 text-sm hover:text-white transition-colors cursor-pointer">
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Empresa + Legal */}
          <div>
            <h4 className="text-white text-sm font-semibold uppercase tracking-widest mb-4">Empresa</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/status" className="text-slate-400 text-sm hover:text-white transition-colors cursor-pointer">
                  Estado del sistema
                </Link>
              </li>
              <li>
                <Link to="/contacto" className="text-slate-400 text-sm hover:text-white transition-colors cursor-pointer">
                  Contacto
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="text-slate-400 text-sm hover:text-white transition-colors cursor-pointer">
                  Privacidad
                </Link>
              </li>
              <li>
                <Link to="/seguridad" className="text-slate-400 text-sm hover:text-white transition-colors cursor-pointer">
                  Seguridad
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-slate-400 text-sm hover:text-white transition-colors cursor-pointer">
                  Términos
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-slate-800 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm">
            © 2026 emite.do. Todos los derechos reservados. República Dominicana.
          </p>
          <div className="flex items-center gap-4">
            <a href="#" rel="nofollow" className="text-slate-500 hover:text-white transition-colors cursor-pointer">
              <Github size={18} />
            </a>
            <a href="#" rel="nofollow" className="text-slate-500 hover:text-white transition-colors cursor-pointer">
              <Linkedin size={18} />
            </a>
            <a href="#" rel="nofollow" className="text-slate-500 hover:text-white transition-colors cursor-pointer">
              <Twitter size={18} />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}